import { useRouteLoaderData } from 'react-router';
import type { MeResponse, TenantSummaryResponse } from '@hefesto/contracts';

/**
 * Portal del cliente (03A) en el navegador. El contexto de la empresa (`GET /api/v1/tenants/{ref}`)
 * lo carga el loader de la ruta `/t/:tenantSlug` (chunk perezoso) y lo leen el shell (navegación) y
 * las páginas. "La interfaz oculta, el backend decide": estas reglas SOLO deciden qué se muestra;
 * cada endpoint vuelve a autorizar. Roles y permisos son SOLO los de esa empresa.
 */
export const PORTAL_ROUTE_ID = 'portal';
export type PortalContext = TenantSummaryResponse;

/** Contexto de la empresa en curso (`undefined` fuera de `/t/:tenantSlug`). */
export function usePortalContext(): PortalContext | undefined {
  return useRouteLoaderData(PORTAL_ROUTE_ID) as PortalContext | undefined;
}

export const portalPath = (slug: string, path = '') => `/t/${slug}${path}`;

export function portalCan(ctx: PortalContext, permission: string): boolean {
  return ctx.permissions.includes(permission);
}

/**
 * ¿Se OFRECE una escritura? Nunca con un acceso de soporte (solo lectura) ni con la empresa
 * suspendida (salvo soporte, avisos y (03B) decidir sobre los accesos de soporte de Mot's, que el
 * backend permite igual). Nunca un botón que falle.
 */
export function portalCanWrite(ctx: PortalContext, permission: string): boolean {
  if (ctx.readOnly || !portalCan(ctx, permission)) return false;
  if (ctx.status === 'active') return true;
  return (
    ctx.status === 'suspended' &&
    (permission.startsWith('support.') ||
      permission.startsWith('notifications.') ||
      permission === 'tenant.support_access.manage')
  );
}

export interface PortalDestination {
  slug: string;
  name: string;
  kind: 'membership' | 'support_access';
  status: 'active' | 'suspended' | 'deactivated';
  roles: string[];
  expiresAt: string | null;
}

/**
 * Empresas a las que se puede entrar: membresías activas (de empresas no desactivadas) y accesos
 * temporales de soporte vigentes. Una membresía suspendida no entra (el backend respondería 404).
 */
export function portalDestinations(me: MeResponse): PortalDestination[] {
  const out: PortalDestination[] = me.memberships
    .filter((m) => m.status === 'active' && m.tenant.status !== 'deactivated')
    .map((m) => ({
      slug: m.tenant.slug,
      name: m.tenant.name,
      kind: 'membership' as const,
      status: m.tenant.status,
      roles: m.roles.map((r) => r.name),
      expiresAt: null,
    }));
  for (const s of me.supportAccess)
    if (!out.some((d) => d.slug === s.tenant.slug))
      out.push({
        slug: s.tenant.slug,
        name: s.tenant.name,
        kind: 'support_access',
        status: 'active',
        roles: [],
        expiresAt: s.expiresAt,
      });
  return out;
}

/** Si hay UNA sola empresa y la persona no es del equipo de Mot's, el inicio va directo a ella. */
export function singlePortalTarget(me: MeResponse): string | null {
  if (me.platform?.status === 'active') return null;
  const all = portalDestinations(me);
  return all.length === 1 ? portalPath(all[0]!.slug) : null;
}
